import * as React from 'react'
import moment from 'moment'
import { browserHistory, Link } from 'react-router'
import { inject, observer } from 'mobx-react'
import {
  IBaseTransfer,
  CreateOrUpdateTransfer,
  ITransfer,
  TransferStatus,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { AppView, AppMainView } from '../../../../../components/AppView'
import { StatusBadge } from '../../../../../components/StatusBadge'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import AppHeader from '../../../../../components/AppView/Header'
import { Routes } from '../../../../../constants/Routes'
import AppPagination from '../../../../../components/Pagination/index'
import { TransferPagination } from './TransferPagination'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { StatusFilters } from './StatusFilters'
import { Skeleton, message, Button } from 'antd'
import { Tooltip } from 'antd-v4'
import { NoPermissionsResult } from '../../../../../components/NoPermissionsResult'
import ButtonGroup from 'antd/lib/button/button-group'
import { HelpLinkMenu } from '../../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import Helmet from 'react-helmet'
import { CreateTransferModal } from './CreateTransferModal'
import { Currency } from '../../../../../components/Currency'
import { TransfersModels } from '@getgreenline/transfers'
import { HelpLinks } from '../../../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface State {
  total: number
  transfers: TransfersModels.IBaseTransferWithTotal[] | undefined
  showCreateTransferModal: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardTransfer extends React.Component<Props, State> {
  pagination: TransferPagination

  company = this.props.currentCompanyStore!.company!
  canEditTransfers = this.props.currentUserStore.hasPermissionAtEntity(
    PermissionNames.CAN_EDIT_TRANSFERS,
    this.company.id,
  )

  constructor(props: Props) {
    super(props)

    this.state = {
      total: 0,
      transfers: undefined,
      showCreateTransferModal: false,
    }

    // Populate filter based off of url query parameters
    this.pagination = new TransferPagination(browserHistory)
  }

  async componentDidMount() {
    this.loadTransfers()
    this.props.currentCompanyStore!.getLocations()
  }

  loadTransfers = async () => {
    this.pagination.applyFilters()

    await this.setState({ transfers: undefined })
    const { transfers, total } = await this.props.currentCompanyStore.getTransfers(
      this.pagination.getFilters(),
    )

    this.setState({ transfers, total })
  }

  setPage(pageIndex: number) {
    const newOffset = pageIndex * this.pagination.paginationFilter.limit
    this.pagination.setOffset(newOffset)
    this.pagination.applyFilters()
    this.loadTransfers()
  }

  onTitleClick = (transferId: string) => {
    browserHistory.push({
      pathname: Routes.EDIT_INVENTORY_BULK_TRANSFER(this.company.id, transferId),
      query: this.pagination.getFilters(),
    })
  }

  duplicateTransfer = async (transfer: IBaseTransfer) => {
    try {
      const originalTransfer = await this.props.currentCompanyStore!.getTransferById(transfer.id)
      const duplicateTransfer = this.createDuplicateTransfer(originalTransfer)
      const createdTransfer = await this.props.currentCompanyStore!.createTransfer(
        duplicateTransfer,
      )
      browserHistory.push(Routes.EDIT_INVENTORY_BULK_TRANSFER(this.company.id, createdTransfer.id))
    } catch (error) {
      message.error(
        parseErrorMsg(error) || `There was an error while duplicating ${transfer.title}`,
      )
    }
  }

  createDuplicateTransfer = (transfer: ITransfer) => {
    const createTransfer = new CreateOrUpdateTransfer()
    createTransfer.populate(transfer)
    createTransfer.id = null
    createTransfer.title = createTransfer.title + ' (duplicate)'
    createTransfer.status = TransferStatus.PENDING
    return createTransfer
  }

  get pageCount(): number {
    const { limit } = this.pagination.paginationFilter
    return Math.ceil(this.state.total / limit)
  }

  get currentPage(): number {
    const { limit, offset } = this.pagination.paginationFilter
    return Math.ceil(offset / limit)
  }

  render() {
    const locations = this.props.currentCompanyStore!.locations || []
    const { transfers } = this.state
    const userCanViewTransfers = this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_TRANSFERS,
      this.company.id,
    )
    const userCanEdit = this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_TRANSFERS,
      this.company.id,
    )

    if (!userCanViewTransfers) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_TRANSFERS} />
    }

    return (
      <AppView column>
        <Helmet>
          <title>Inventory | Transfers</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Transfers'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={{
                href: HelpLinks.TRANSFERS,
                title: 'Transfers',
              }}
            />

            <Button
              type='primary'
              icon='plus'
              style={{ borderTopRightRadius: 3, borderBottomRightRadius: 3 }}
              disabled={!userCanEdit}
              onClick={() => this.setState({ showCreateTransferModal: true })}
            >
              Add a transfer
            </Button>

            <CreateTransferModal
              visible={this.state.showCreateTransferModal}
              onClose={() => this.setState({ showCreateTransferModal: false })}
            />
          </span>
        </AppHeader>

        <AppMainView className='background-grey intercom-pb-2'>
          <StatusFilters
            pagination={this.pagination}
            locations={locations}
            loadTransfers={this.loadTransfers}
          />

          <br />

          {transfers ? (
            <div className='table-responsive background-white'>
              <table className='table'>
                <thead className='thead-light'>
                  <tr>
                    <th>Title</th>
                    <th>Source</th>
                    <th>Destination</th>
                    <th>Created</th>
                    <th>Completed</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {(this.state.transfers || []).map((transfer) => {
                    return (
                      <tr key={transfer.id}>
                        <td>
                          <div className='text-muted'>
                            <span
                              style={{ cursor: 'pointer' }}
                              onClick={() => this.onTitleClick(transfer.id)}
                            >
                              {transfer.id}
                            </span>
                          </div>

                          <div>
                            <span>
                              <a onClick={() => this.onTitleClick(transfer.id)}>{transfer.title}</a>
                            </span>
                          </div>
                        </td>
                        <td>{transfer.fromLocationName}</td>
                        <td>{transfer.toLocationName}</td>
                        <td>{moment(transfer.createDate).format('MMM Do YYYY')}</td>
                        <td>
                          {transfer.completeDate
                            ? moment(transfer.completeDate).format('MMM Do YYYY')
                            : '-'}
                        </td>
                        <td>
                          <Currency centValue={transfer.total} />
                        </td>
                        <td>
                          <StatusBadge status={transfer.status} />
                        </td>
                        <td>
                          <UncontrolledDropdown size='sm'>
                            <DropdownToggle color='light'>
                              <i className='fa fas fa-ellipsis-v' />
                            </DropdownToggle>
                            <DropdownMenu className='background-shadow'>
                              <DropdownItem
                                disabled={!this.canEditTransfers}
                                onClick={() => this.duplicateTransfer(transfer)}
                                style={{
                                  cursor: !this.canEditTransfers ? 'not-allowed' : 'pointer',
                                }}
                              >
                                <Tooltip
                                  trigger={['click']}
                                  placement='left'
                                  title={'insufficient permissions'}
                                >
                                  <span>Duplicate</span>
                                </Tooltip>
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Skeleton active />
          )}

          <br />

          <AppPagination
            currentPage={this.currentPage}
            pageCount={this.pageCount}
            onPageChange={(data) => this.setPage(data.selected)}
          />
        </AppMainView>
      </AppView>
    )
  }
}
